/** @jsxImportSource @emotion/react */
import { useSelector } from "react-redux";
import { IAppState } from "../../../core/redux/IAppState";
import { formatDate, formatTimeOnly } from "../../../language/time";
import { css } from "@emotion/react";
import { BLUE_CREEK } from "../../../styles/colors";
import { getResource } from "../../../language/resources/getResource";
import {
  formatTimeRange,
  getWeekdayLabel,
  formatAllMultiLine,
  formatLimitedMultiLine,
  formatOneLine,
} from "../../module";
import * as React from "react";
import { BlackoutDateRangeAccessRule } from "../blackoutDateRange/blackoutDateRange";
import { BlackoutDayOfWeekAccessRule } from "../blackoutDayOfWeek/blackoutDayOfWeek";
import { LiftAccessRuleSet } from "./models";
import { ActionMenu } from "../../../forms/elements/ActionMenu";
import { ReactComponent as EditIcon } from "../../../assets/icons/pencil.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/trashcan.svg";

const MainFontSize = 13;
const SmallFontSize = 11;

export const SelectWidth = 32;
export const IDWidth = 45;
export const DescriptionWidth = 250;
export const SeasonWidth = 80;
export const FulfillmentSeasonWidth = 120;
export const LocationWidth = 175;
export const EffectiveDateWidth = 85;
export const ExpirationDateWidth = 85;
export const EffectiveTimeWidth = 100;
export const MaxDaysWidth = 65;
export const BlackoutDateRangeWidth = 175;
export const BlackoutDOWWidth = 80;
export const MenuOptionsWidth = 22;

const selectCellCss = css`
  width: ${SelectWidth}px !important;
  max-width: ${SelectWidth}px !important;
  text-align: center;

  input {
    cursor: pointer;
  }
`;

const idCellCss = css`
  font-size: ${MainFontSize}px;
  width: ${IDWidth}px !important;
  max-width: ${IDWidth}px !important;
`;

const descriptionCellCss = css`
  width: ${DescriptionWidth}px !important;
  min-width: 0;
  font-size: ${MainFontSize}px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const seasonCellCss = css`
  width: ${SeasonWidth}px !important;
  max-width: ${SeasonWidth}px !important;
  font-size: ${MainFontSize}px;
  text-align: center;
`;

const fulfillmentSeasonCellCss = css`
  width: ${FulfillmentSeasonWidth}px !important;
  max-width: ${FulfillmentSeasonWidth}px;
  font-size: ${MainFontSize}px;
`;

const locationCellCss = css`
  min-width: 0;
  width: ${LocationWidth}px !important;
  font-size: ${MainFontSize}px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const effectiveDateCellCss = css`
  font-size: ${SmallFontSize}px;
  width: ${EffectiveDateWidth}px !important;
  max-width: ${EffectiveDateWidth}px;
  white-space: pre-line;
`;

const expirationDateCellCss = css`
  font-size: ${SmallFontSize}px;
  width: ${ExpirationDateWidth}px !important;
  max-width: ${ExpirationDateWidth}px;
  white-space: pre-line;
`;

const effectiveTimeCellCss = css`
  font-size: ${SmallFontSize}px;
  width: ${EffectiveTimeWidth}px !important;
  max-width: ${EffectiveTimeWidth}px;
  white-space: pre-wrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const maxDaysCellCss = css`
  text-align: center;
  white-space: pre-wrap;
  font-size: ${MainFontSize}px;
  width: ${MaxDaysWidth}px !important;
  max-width: ${MaxDaysWidth}px;

  .line {
    display: flex;
    justify-content: center;
    gap: 2px;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    pointer-events: none;
  }

  .asterisk {
    color: ${BLUE_CREEK};
    font-weight: bold;
    font-size: 19px;
    text-align: left;
    position: relative;
    pointer-events: auto;
    top: -3px; /* adjust up or down */
  }

  .asterisk.empty {
    visibility: hidden; /* keeps number alignment */
  }
`;

const blackoutDateRangeCellCss = css`
  width: ${BlackoutDateRangeWidth}px !important;
  max-width: ${BlackoutDateRangeWidth}px;
  white-space: pre-wrap;
  font-size: ${SmallFontSize}px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const blackoutDOWCellCss = css`
  white-space: pre-wrap;
  font-size: ${SmallFontSize}px;
  width: ${BlackoutDOWWidth}px !important;
  max-width: ${BlackoutDOWWidth}px;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const menuCellCss = css`
  width: ${MenuOptionsWidth}px !important;
  max-width: ${MenuOptionsWidth}px;
`;

const menuContainerCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

/**
 * Builds the display values for blackout date ranges, optionally including time ranges in the tooltip.
 * Sorts the rules by start date and formats each entry as "Start – End" or just "Start" if end date is missing.
 * Time range is formatted using the provided timeFormat and included in the tooltip for more details.
 */
function buildBlackoutDateRanges(
  blackoutDateRangeRules: BlackoutDateRangeAccessRule[] | undefined,
  dateFormat: string,
  timeFormat: string,
) {
  return (blackoutDateRangeRules ?? [])
    .slice()
    .sort((a, b) => {
      const startA = new Date(a.blackoutDateStart).getTime();
      const startB = new Date(b.blackoutDateStart).getTime();
      if (startA !== startB) return startA - startB;

      const endA = new Date(a.blackoutDateEnd).getTime();
      const endB = new Date(b.blackoutDateEnd).getTime();
      return endA - endB;
    })
    .map((r) => {
      const startDate = r.blackoutDateStart
        ? formatDate(r.blackoutDateStart, dateFormat)
        : "";

      const endDate = r.blackoutDateEnd
        ? formatDate(r.blackoutDateEnd, dateFormat)
        : "";

      const dateOnly = startDate && endDate ? `${startDate} – ${endDate}` : "";

      const timeRange = formatTimeRange(
        r.blackoutTimeSlotStart,
        r.blackoutTimeSlotEnd,
        timeFormat,
        true,
      );

      // Add time range only in the tooltip version
      const dateWithTime =
        timeRange && startDate && endDate
          ? `${startDate} – ${endDate} (${timeRange})`
          : dateOnly;

      return {
        dateOnly,
        dateWithTime,
      };
    });
}

/**
 * Builds the display values for blackout days of week, optionally including time ranges.
 * Sorts the rules by dayOfWeekId (0-6) and formats each entry as "Weekday (TimeRange)" or just "Weekday".
 * Time range is formatted using the provided timeFormat and localized weekday names are used.
 */
function buildBlackoutDOW(
  blackoutDayOfWeekRules: BlackoutDayOfWeekAccessRule[] | undefined,
  resources: any,
  timeFormat: string,
) {
  return (blackoutDayOfWeekRules ?? [])
    .slice()
    .sort((a, b) => a.dayOfWeekId - b.dayOfWeekId)
    .map((r) => {
      const weekday = getWeekdayLabel(r.dayOfWeekId, resources);
      const time = formatTimeRange(
        r.blackoutTimeSlotStart,
        r.blackoutTimeSlotEnd,
        timeFormat,
        true,
      );

      return {
        weekdayOnly: weekday,
        weekdayAndTime: time ? `${weekday} (${time})` : weekday,
      };
    });
}

type LiftAccessRuleSetRowProps = {
  ruleSet: LiftAccessRuleSet;
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: (
    ruleSet: LiftAccessRuleSet,
    selected: boolean,
    shiftKey: boolean,
  ) => void;
  /** Readable and sortable as usual, but selection and row actions are off. */
  locked?: boolean;
  onDoubleClick?: (ruleSet: LiftAccessRuleSet) => void;
  onEdit?: (ruleSet: LiftAccessRuleSet) => void;
  onDelete?: (ruleSet: LiftAccessRuleSet) => void;
};

export const LiftAccessRuleSetRow = ({
  ruleSet,
  selectable,
  selected,
  onToggleSelect,
  locked,
  onDoubleClick,
  onEdit,
  onDelete,
}: LiftAccessRuleSetRowProps) => {
  const resources = useSelector((state: IAppState) => state.locale);
  const dateFormat = useSelector((state: IAppState) => state.defaultDateFormat);
  const timeFormat = useSelector((state: IAppState) => state.defaultTimeFormat);

  const effectiveDate = React.useMemo(
    () =>
      (ruleSet.effectiveDateRangeRules ?? []).map((r) =>
        r.effectiveDateStart
          ? formatDate(r.effectiveDateStart, dateFormat)
          : "",
      ),
    [ruleSet.effectiveDateRangeRules, dateFormat],
  );

  const expirationDate = React.useMemo(
    () =>
      (ruleSet.effectiveDateRangeRules ?? []).map((r) =>
        r.effectiveDateEnd ? formatDate(r.effectiveDateEnd, dateFormat) : "",
      ),
    [ruleSet.effectiveDateRangeRules, dateFormat],
  );

  const effectiveTime = React.useMemo(
    () =>
      (ruleSet.effectiveDateRangeRules ?? []).map((r) =>
        r.effectiveTimeSlotStart && r.effectiveTimeSlotEnd
          ? `${formatTimeOnly(r.effectiveTimeSlotStart, timeFormat, true)} – ${formatTimeOnly(
              r.effectiveTimeSlotEnd,
              timeFormat,
              true,
            )}`
          : "",
      ),
    [ruleSet.effectiveDateRangeRules, timeFormat],
  );

  const blackoutDateRanges = React.useMemo(
    () =>
      buildBlackoutDateRanges(
        ruleSet.blackoutDateRangeRules,
        dateFormat,
        timeFormat,
      ),
    [ruleSet.blackoutDateRangeRules, dateFormat, timeFormat],
  );

  const blackoutDateRangeDisplay = React.useMemo(
    () => formatLimitedMultiLine(blackoutDateRanges.map((x) => x.dateOnly)),
    [blackoutDateRanges],
  );

  const blackoutDateRangeTitle = React.useMemo(
    () => formatAllMultiLine(blackoutDateRanges.map((x) => x.dateWithTime)),
    [blackoutDateRanges],
  );

  const blackoutDOW = React.useMemo(
    () =>
      buildBlackoutDOW(ruleSet.blackoutDayOfWeekRules, resources, timeFormat),
    [ruleSet.blackoutDayOfWeekRules, resources, timeFormat],
  );

  const blackoutDOWDisplay = React.useMemo(() => {
    const uniqueDays = new Set<string>();

    return formatOneLine(
      blackoutDOW
        .map((x) => x.weekdayOnly)
        .filter((day) => day && !uniqueDays.has(day) && uniqueDays.add(day)),
    );
  }, [blackoutDOW]);

  const blackoutDOWTitle = React.useMemo(
    () => formatAllMultiLine(blackoutDOW.map((x) => x.weekdayAndTime)),
    [blackoutDOW],
  );

  const maxDaysDisplay = React.useMemo(() => {
    const rules = ruleSet.maxDaysRules ?? [];
    if (rules.length === 0) return [{ num: "", shared: false, tooltip: "" }];

    const items = rules.map((r) => ({
      num: String(r.numberOfDays),
      shared: r.sharedUsage,
      tooltip: r.sharedUsage
        ? getResource(resources, "LIFT_ACCESS_RULES_TABLE_SHARED")
        : "",
    }));

    const limited = items.length > 3 ? items.slice(0, 3) : items;

    if (items.length > 3) {
      limited.push({ num: "...", shared: false, tooltip: "" });
    }

    return limited;
  }, [ruleSet.maxDaysRules, resources]);

  return (
    <tr
      css={css`
        cursor: ${locked ? "default" : "pointer"};
      `}
      tabIndex={0}
      onDoubleClick={locked ? undefined : () => onDoubleClick?.(ruleSet)}
    >
      {selectable && (
        <td
          css={selectCellCss}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            aria-label={getResource(resources, "ACCESS_RULE_SELECT_ROW")}
            title={getResource(resources, "ACCESS_RULE_SELECT_ROW")}
            checked={!!selected}
            disabled={locked}
            onChange={() => {
              // Selection is handled in onClick (to read the Shift modifier);
              // onChange just satisfies React's controlled-input contract.
            }}
            onClick={(e) =>
              onToggleSelect?.(ruleSet, e.currentTarget.checked, e.shiftKey)
            }
          />
        </td>
      )}

      <td css={idCellCss} title={ruleSet.entitlementTypeId.toString()}>
        {ruleSet.entitlementTypeId}
      </td>

      <td css={descriptionCellCss} title={ruleSet.entitlementDescription}>
        {ruleSet.entitlementDescription}
      </td>

      <td css={seasonCellCss} title={ruleSet.sellingSeasonDescription}>
        {ruleSet.sellingSeasonDescription}
      </td>

      <td
        css={fulfillmentSeasonCellCss}
        title={ruleSet.fulfillmentSeasonDescription}
      >
        {ruleSet.fulfillmentSeasonDescription}
      </td>

      <td css={locationCellCss} title={ruleSet.locationName}>
        {ruleSet.locationName}
      </td>

      <td css={effectiveDateCellCss} title={formatAllMultiLine(effectiveDate)}>
        {formatLimitedMultiLine(effectiveDate)}
      </td>

      <td
        css={expirationDateCellCss}
        title={formatAllMultiLine(expirationDate)}
      >
        {formatLimitedMultiLine(expirationDate)}
      </td>

      <td css={effectiveTimeCellCss} title={formatAllMultiLine(effectiveTime)}>
        {formatLimitedMultiLine(effectiveTime)}
      </td>

      <td
        css={maxDaysCellCss}
        title={maxDaysDisplay.map((x) => x.num).join(", ")}
      >
        {maxDaysDisplay.map((item, idx) => (
          <div key={idx} className="line" title={item.tooltip}>
            <span>{item.num}</span>
            <span className={item.shared ? "asterisk" : "asterisk empty"}>
              *
            </span>
          </div>
        ))}
      </td>

      <td css={blackoutDateRangeCellCss} title={blackoutDateRangeTitle}>
        {blackoutDateRangeDisplay}
      </td>

      <td css={blackoutDOWCellCss} title={blackoutDOWTitle}>
        {blackoutDOWDisplay}
      </td>

      <td css={menuCellCss}>
        <div css={menuContainerCss}>
          <ActionMenu
            moreOptionsTitle={getResource(resources, "ACCESS_RULE_OPTIONS")}
            actions={[
              {
                id: "edit",
                label: getResource(resources, "ACCESS_RULE_EDIT"),
                icon: <EditIcon />,
                // Both open a dialog of their own, which would displace one
                // already open in a separate window.
                disabled: locked,
                onClick: () => onEdit?.(ruleSet),
              },
              {
                id: "delete",
                label: getResource(resources, "ACCESS_RULE_DELETE"),
                icon: <DeleteIcon />,
                disabled: locked,
                onClick: () => onDelete?.(ruleSet),
              },
            ]}
          />
        </div>
      </td>
    </tr>
  );
};
